import React from 'react';
import { useTranslation } from 'react-i18next';

import loadable from '@loadable/component';

import {
  FundraisingPageFragment,
  PageStatus,
} from '../../../types/__generated__';
import config from '../../client/config';
import AboutCampaignAndCharitySection from '../../components/AboutCampaignAndCharitySection/AboutCampaignAndCharitySection';
import AboutCauseMini from '../../components/AboutCauseMini/AboutCauseMini';
import { ActivityTotals } from '../../components/ActivityTotals/ActivityTotals';
import { Banner } from '../../components/Banner/Banner';
import { DonateToYourselfModal } from '../../components/DonateToYourselfModal/DonateToYourselfModal';
import DonationSummary from '../../components/DonationSummary/DonationSummary';
import EventDetails from '../../components/EventDetails/EventDetails';
import FeatureImage from '../../components/FeatureImage/FeatureImage';
import FundraisingCTA from '../../components/FundraisingCTA/FundraisingCTA';
import FundraisingHelmet from '../../components/Helmet/FundraisingHelmet';
import IsPageOwner from '../../components/IsPageOwner/IsPageOwner';
import IsQRCode from '../../components/IsQRCode/IsQRCode';
import DefaultLayout from '../../components/Layout/DefaultLayout';
import Page from '../../components/Page/Page';
import PageAlert from '../../components/PageAlert/PageAlert';
import PageOwnerAvatar from '../../components/PageOwnerAvatar/PageOwnerAvatar';
import PageOwnerTitle from '../../components/PageOwnerTitle/PageOwnerTitle';
import PageSection from '../../components/PageSection/PageSection';
import Reason from '../../components/Reason/Reason';
import { ShareModal } from '../../components/ShareModal/ShareModal';
import FundraisingSocialButtons from '../../components/SocialButtons/FundraisingSocialButtons';
import Story from '../../components/Story/Story';
import SupportersList from '../../components/SupportersList/SupportersList';
import TeamDetails from '../../components/TeamDetails/TeamDetails';
import TotaliserWrapper from '../../components/TotaliserWrapper/TotaliserWrapper';
import { FirstToDonateTotals } from '../../components/Totals/FirstToDonateTotals';
import { Totals } from '../../components/Totals/Totals';
import VideoStoryCheckList from '../../components/VideoStoryCheckList/VideoStoryCheckList';
import { MODAL } from '../../context';
import {
  buildQuery,
  useApp,
  useFlags,
  usePageMeta,
  usePageOwner,
  usePageQuery,
  useShareUrls,
} from '../../hooks';
import { useOpenDonateToYourselfModal } from '../../hooks/fundraising/useOpenDonateToYourselfModal';
import { useInView } from '../../hooks/useinView';
import { useScript } from '../../hooks/useScript';
import { useShouldDisplayFirstToDonateTotals } from '../../hooks/useShouldDisplayFirstToDonateTotals';
import Fragment from './FundraisingPageFragment.gql';

import styles from './FundraisingPage.scss';

const Prompt = loadable(() => import('../../components/Prompt/Prompt'));
const QRModal = loadable(() => import('../../components/QRModal/QRModal'));
const Editbar = loadable(() => import('../../components/Editbar/Editbar'));
const TimelinePageSection = loadable(() =>
  import('../../components/Timeline/TimelinePageSection/TimelinePageSection'),
);
const FitnessSection = loadable(
  () => import('../../components/FitnessSection/FitnessSection'),
  { resolveComponent: module => module.FitnessSection },
);
const GoToImmersiveGiving = loadable(
  () => import('../../components/GoToImmersiveGiving/GoToImmersiveGiving'),
  {
    resolveComponent: module => module.GoToImmersiveGiving,
  },
);
const TeamAndCompany = loadable(() =>
  import('../../components/TeamAndCompany/TeamAndCompany'),
);
const QualarooLoader = loadable(
  () => import('../../components/QualarooLoader/QualarooLoader'),
  { resolveComponent: module => module.QualarooLoader },
);
const StickySummary = loadable(() =>
  import('../../components/StickySummary/StickySummary'),
);
const EncourageInactivityModal = loadable(() =>
  import('../../components/EncourageInactivityModal/EncourageInactivityModal'),
);

const FundraisingPageQuery = buildQuery(Fragment);

export const FundraisingPage: React.FunctionComponent = () => {
  useScript('//rum-static.pingdom.net/pa-5e3af8dc229e9300080003d1.js', {
    async: true,
  });
  const { inViewRef, inView, rootIsBelowTarget } = useInView();
  const { data } = usePageQuery<FundraisingPageFragment>(FundraisingPageQuery);
  const { t } = useTranslation('common', { useSuspense: false });
  const { pageShortName } = usePageMeta();
  const { enableOtgImmersiveGiving, useOTGVideoGenerator } = useFlags();
  useOpenDonateToYourselfModal({
    totalDonationAmount: data?.donationSummary?.totalAmount?.value,
  });

  const shouldDisplayFirstToDonateTotals = useShouldDisplayFirstToDonateTotals();

  const name = data?.owner?.name ?? '';
  const eventGivingGroupId = data?.eventGivingGroupId;
  const totalRelatedCompanies = data?.relationships?.companies.totalCount ?? 0;

  const displayTeamAndCompany =
    totalRelatedCompanies > 0 ||
    data?.relationships?.teams.nodes[0]?.status === PageStatus.Active;

  const adminLink = `${config.baseUri}/account/your-pages/${pageShortName}?pageId=${eventGivingGroupId}`;

  const { hideModal, openModal } = useApp();
  const { isPageOwner } = usePageOwner();
  const shareUrls = useShareUrls();

  return (
    <Page>
      {openModal === MODAL.DONATE_TO_YOURSELF && <DonateToYourselfModal />}
      {openModal === MODAL.SHARE && shareUrls && (
        <ShareModal
          heading={
            isPageOwner
              ? t('shareModal.heading.pageOwner')
              : t('shareModal.heading', {
                  pageOwner: data?.owner?.name,
                })
          }
          description={t('shareModal.description')}
          onClose={hideModal}
          shareItems={shareUrls}
        />
      )}
      <IsQRCode>
        <QRModal ownerName={name} />
      </IsQRCode>
      <PageAlert />
      <Banner />
      {eventGivingGroupId && (
        <IsPageOwner>
          <Prompt entityId={eventGivingGroupId.toString()} />
        </IsPageOwner>
      )}
      <Editbar adminLink={adminLink} />
      <FundraisingHelmet />
      <DefaultLayout
        slots={{
          featureImage: <FeatureImage enableLightbox />,
          ownerAvatar: <PageOwnerAvatar />,
          titles: <PageOwnerTitle />,
          totals: (
            <>
              <TotaliserWrapper />
              {shouldDisplayFirstToDonateTotals ? (
                <FirstToDonateTotals />
              ) : (
                <Totals />
              )}
            </>
          ),
          callsToAction: (
            <div ref={inViewRef} className={styles.ctaWrapper}>
              <FundraisingCTA />
            </div>
          ),
          titlesSupplementary: (
            <div className="jg-space-mbxs jg-space-mbsm@xl jg-space-mbn jg-space-mtxs jg-space-mtsm@xl">
              <EventDetails />
              <Reason />
              <TeamDetails />
            </div>
          ),
          causeMini: <AboutCauseMini />,
          main: (
            <>
              <PageSection
                title={t('story.title')}
                className="jg-pages-fundraising__story"
                hasBorder={true}
              >
                <Story />
                <h3 className="jg-text--center">
                  {t('story.shareTitle', 'Share this story')}
                </h3>
                <FundraisingSocialButtons />
              </PageSection>
              <TimelinePageSection />
            </>
          ),
          aside: (
            <>
              {enableOtgImmersiveGiving &&
                data?.relationships?.campaigns.nodes[0]?.slug ===
                  'make-a-refugee-shelter-a-home' && <GoToImmersiveGiving />}
              {useOTGVideoGenerator && (
                <IsPageOwner>
                  <PageSection>
                    <VideoStoryCheckList />
                  </PageSection>
                </IsPageOwner>
              )}
              <IsPageOwner>
                <PageSection>
                  <FitnessSection />
                </PageSection>
              </IsPageOwner>
              <ActivityTotals />
              <SupportersList
                noSupportersMessage={t(
                  'fundraising.noSupportersMessage',
                  `Become {{name}}'s first supporter by making a donation`,

                  { replace: { name } },
                )}
                displayDonateButton
                donateButtonExperiments={[]}
              />
            </>
          ),
          teamAndCompany: displayTeamAndCompany && <TeamAndCompany />,
          contentFooterPrimary: <AboutCampaignAndCharitySection />,
          contentFooterSecondary: (
            <DonationSummary
              fieldsToShow={['onlineAmount', 'offlineAmount', 'textDonations']}
            />
          ),
        }}
      />
      {!inView && rootIsBelowTarget && (
        <StickySummary donateButtonExperiments={[]} />
      )}
      <EncourageInactivityModal />
      <QualarooLoader />
    </Page>
  );
};

FundraisingPage.displayName = 'FundraisingPage';

export default FundraisingPage;
